import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from 'store';
import {
  closeModal,
  deleteCoopEscolar,
  getCoopsEscolar,
  handleChangeProgramaDisabled,
  putCoopEscolar,
  saveCoopEscolar,
} from 'store/modules/EscolasActions';
import languageValues from 'utils/LanguageUtils';
import SnackbarUtils from 'utils/SnackbarUtils';
import useCadastroSteps from './useEscolaSteps';
import { DocumentItem } from '../listaPrograma/listaPeriodo/listaPeriodoItem/cooperativa/Modal/ModalAddCoop/DocumentCoop/__types';
import { hasLargeFile } from 'utils/FileUtils';
import { TipoPrograma } from 'store/modules/EscolasTypes';
import { SchoolEditParams } from '../__types';

export default function UseCooperativa() {
  const dispatch = useDispatch();
  const { id } = useParams<SchoolEditParams>();
  const programas = useSelector((state: RootState) => state.escolas.programas);

  const { handleProgramaInfoFetch } = useCadastroSteps();

  const handleDisableProgramaCoop = useCallback(() => {
    if (!programas[TipoPrograma.CooperativaEscolar]?.disabled) {
      dispatch(
        handleChangeProgramaDisabled(TipoPrograma.CooperativaEscolar, true)
      );
    }
  }, [dispatch, programas]);

  const handleFilesCreation = useCallback(
    (documentItems: DocumentItem[], removedFiles: number[]) => {
      const fileRemoved: number[] = [];
      const fileTypes: number[] = [];
      const files: File[] = [];

      documentItems.forEach((item) => {
        if (item.file != null && item.uploaded) {
          fileTypes.push(item.type);
          files.push(item.file);
        }
      });
      removedFiles.forEach((removedItem) => {
        if (!fileTypes.includes(removedItem)) fileRemoved.push(removedItem);
      });

      return { fileTypes, files, fileRemoved };
    },
    []
  );

  const handleCoopsFetch = useCallback(() => {
    dispatch(getCoopsEscolar(id));
  }, [dispatch, id]);

  const handleCoopSave = useCallback(
    (formData: FormData) => {
      const maxSize = 31 * 1024 * 1024;
      if (hasLargeFile(formData, maxSize)) {
        SnackbarUtils.error(languageValues.pages.escolas.snackbar.coopSalvarFalhaTamanhoArquivo);
      } else {
        dispatch(
          saveCoopEscolar(
            id,
            formData,
            () => {
              dispatch(closeModal());
              handleCoopsFetch();
              handleDisableProgramaCoop();
              SnackbarUtils.success(
                languageValues.pages.escolas.snackbar.coopSalvarSucesso
              );
            },
            () => {
              SnackbarUtils.error(
                languageValues.pages.escolas.snackbar.coopSalvarFalha
              );
            }
          )
        );
      }
    },
    [dispatch, handleCoopsFetch, handleDisableProgramaCoop, id]
  );

  const handleCoopDelete = useCallback(
    (idCoopDelete: number) => {
      dispatch(
        deleteCoopEscolar(
          idCoopDelete.toString(),
          () => {
            handleProgramaInfoFetch(TipoPrograma.CooperativaEscolar);
            handleCoopsFetch();
            dispatch(closeModal());
            SnackbarUtils.success(
              languageValues.pages.escolas.snackbar.coopRemovidaSucesso
            );
          },
          () => {
            SnackbarUtils.error(
              languageValues.pages.escolas.snackbar.coopRemovidaFalha
            );
          }
        )
      );
    },
    [dispatch, handleCoopsFetch, handleProgramaInfoFetch]
  );

  const handleCoopUpdate = useCallback(
    (idCoop: number, formData: FormData) => {
      const maxSize = 31 * 1024 * 1024;
      if (hasLargeFile(formData, maxSize)) {
        SnackbarUtils.error(languageValues.pages.escolas.snackbar.coopSalvarFalhaTamanhoArquivo);
      } else {
        dispatch(
          putCoopEscolar(
            idCoop.toString(),
            formData,
            () => {
              handleCoopsFetch();
              dispatch(closeModal());
              SnackbarUtils.success(
                languageValues.pages.escolas.snackbar.coopEditarSucesso
              );
            },
            () => {
              SnackbarUtils.error(
                languageValues.pages.escolas.snackbar.coopEditarFalha
              );
            }
          )
        );
      }
    },
    [dispatch, handleCoopsFetch]
  );

  const handleDownloadLink = useCallback((file: File) => {
    const url = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', file.name);
    document.body.appendChild(link);
    link.click();
  }, []);

  return {
    handleFilesCreation,
    handleCoopsFetch,
    handleCoopSave,
    handleCoopUpdate,
    handleCoopDelete,
    handleDownloadLink,
    handleDisableProgramaCoop,
  };
}
